import { Dialog, DialogContent } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getConversationDetail, getConversations, markConversationAsRead, reopenConversation, sendMessage, decrementPendingMessagingCount } from "../../../actions/messaging";
import snackbarUtils from "../../../utils/snackbarUtils";
import ConfirmDialog from "../../commons/dialog/confirmDialog";
import NewFormHeader from "../forms/headers/newFormHeader";
import ConversationChatArea from "./conversationChatArea";
import ConversationSidebar from "./conversationSidebar";

const ConversationDetailDialog = ({ open, onClose, conversationId, idArt, idSubtipo, idSiniestro }) => {
  const dispatch = useDispatch();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const { sendingMessage } = useSelector((state) => state.messaging);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (open && conversationId) {
      loadDetail();
    } else {
      setDetail(null);
      setInputText("");
      setReplyTo(null);
      setConfirmCloseOpen(false);
    }
  }, [open, conversationId]);

  const loadDetail = () => {
    setLoading(true);
    dispatch(getConversationDetail(conversationId, idArt, idSubtipo))
      .then((res) => {
        const fetchedDetail = res.value.data;
        setDetail(fetchedDetail);

        const unreadMessages = fetchedDetail.mensajes?.filter(m => m.usuarioCreacion !== "Prestador" && m.idEstadoMensaje === 1) || [];
        if (unreadMessages.length > 0) {
          dispatch(markConversationAsRead(conversationId, idArt, idSubtipo))
            .then(() => {
              dispatch(decrementPendingMessagingCount(unreadMessages.length));
              setDetail(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  mensajes: prev.mensajes.map(m => {
                    if (m.usuarioCreacion !== "Prestador" && m.idEstadoMensaje === 1) {
                      return { ...m, idEstadoMensaje: 2, estadoMensaje: "Leído" };
                    }
                    return m;
                  })
                };
              });
            })
            .catch(console.error);
        }
      })
      .catch((err) => {
        snackbarUtils.error("Error al cargar la conversación");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (detail && detail.mensajes) {
      scrollToBottom();
    }
  }, [detail]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const hasMessageText = inputText.trim().length > 0;

  const requestClose = () => {
    if (hasMessageText) {
      setConfirmCloseOpen(true);
      return;
    }

    onClose();
  };

  const forceClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  const handleSend = () => {
    if (!inputText.trim()) return;

    if (detail.cerrada && detail.puedeReabrir) {
      // Reopen logic
      dispatch(reopenConversation(conversationId, inputText, idArt, detail.idTipo, idSubtipo))
        .then(() => {
          snackbarUtils.success("Conversación reabierta y mensaje enviado");
          setInputText("");
          setReplyTo(null);
          loadDetail();
          if (idSiniestro) dispatch(getConversations(idSiniestro, idArt));
        })
        .catch(() => snackbarUtils.error("Error al reabrir la conversación"));
    } else {
      // Send message
      dispatch(sendMessage(conversationId, inputText, replyTo?.idMensaje, idArt, idSubtipo))
        .then(() => {
          setInputText("");
          setReplyTo(null);
          loadDetail();
          if (idSiniestro) dispatch(getConversations(idSiniestro, idArt));
        })
        .catch(() => snackbarUtils.error("Error al enviar el mensaje"));
    }
  };

  if (!open) return null;

  const isClosedButCannotReopen = detail?.cerrada && !detail?.puedeReabrir;
  const isClosedButCanReopen = detail?.cerrada && detail?.puedeReabrir;
  const canType = detail?.puedeResponder || isClosedButCanReopen;

  // Title formatting logic
  const titleStr = detail ? (detail.idConversacionExterna ? `CONV-${new Date().getFullYear()}-${detail.idConversacionExterna.toString().padStart(6, '0')}` : "Pendiente Asignar") : "Cargando...";

  return (
    <>
      <Dialog open={open} onClose={requestClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: "80vh", maxHeight: "90vh", display: 'flex', flexDirection: 'column' } }}>
        <NewFormHeader formName={`Conversación ${titleStr} ${detail ? `(${detail.estado})` : ""}`} handleClose={requestClose} loading={loading} />

        <DialogContent sx={{ display: "flex", p: 0, height: "100%", overflow: 'hidden' }}>
          {/* Sidebar */}
          <ConversationSidebar detail={detail} />

          {/* Chat Area */}
          <ConversationChatArea
            detail={detail}
            loading={loading}
            messagesEndRef={messagesEndRef}
            canType={canType}
            isClosedButCanReopen={isClosedButCanReopen}
            isClosedButCannotReopen={isClosedButCannotReopen}
            inputText={inputText}
            setInputText={setInputText}
            replyTo={replyTo}
            setReplyTo={setReplyTo}
            sending={sendingMessage?.isLoading}
            handleSend={handleSend}
          />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmCloseOpen}
        handleClose={() => setConfirmCloseOpen(false)}
        handleConfirm={forceClose}
        title="CERRAR VENTANA"
        text="¿Está seguro que desea cancelar esta operación? En caso de aceptar, se perderá el mensaje ingresado."
      />
    </>
  );
};

export default ConversationDetailDialog;
